import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';

import { StartPushBroadcastMutation, StartPushBroadcastMutationVariables } from '../../../../../../__gqltypes__';
import { START_PUSH_BROADCAST } from '../graphql';
import {
  defaultPushSendAt,
  formatPushUtc,
  pushBroadcastErrorIssues,
  PushBroadcastData,
  PushBroadcastOptions,
  PushIssue,
  pushLanguageName,
  PushPerson,
  pushPersonLabel,
  pushRelativeTime,
  pushTranslatedLanguages,
  sourceLanguageOf,
} from '../utils';
import { PushIssueList } from './PushIssueList';
import { PushUtcDateTime } from './PushUtcDateTime';

type Props = {
  broadcast: PushBroadcastData;
  options: PushBroadcastOptions;
  people: ReadonlyMap<string, PushPerson>;
  // Unsaved edits: the send uses the saved broadcast, so they would silently not be sent.
  isDirty: boolean;
  onStarted: (broadcast: PushBroadcastData) => void;
};

/**
 * The send button and its confirm dialog, at the end of the Audience card. Disabled while the backend reports a reason
 * (startBlockers) or while the editor holds unsaved edits, the reasons always shown next to it: never a silently dead
 * button.
 */
export function PushSendPanel({ broadcast, options, people, isDirty, onStarted }: Props): JSX.Element {
  const [showConfirm, setShowConfirm] = useState(false);
  const [issues, setIssues] = useState<PushIssue[]>([]);
  // Null = send now; otherwise the UTC send time (epoch ms).
  const [sendAt, setSendAt] = useState<number | null>(null);
  const [startBroadcast, { loading: starting }] = useMutation<
    StartPushBroadcastMutation,
    StartPushBroadcastMutationVariables
  >(START_PUSH_BROADCAST);

  const { pushBroadcastLanguages: languages, pushBroadcastSettingsCategories: categories } = options;
  const blockers = [
    ...(isDirty ? ['Save your edits first: the send uses the saved broadcast.'] : []),
    ...broadcast.startBlockers,
  ];
  const { audience } = broadcast;
  const sourceLanguage = sourceLanguageOf(languages);
  const translated = pushTranslatedLanguages(broadcast);
  const overrides = translated.filter((code) => code !== sourceLanguage);
  const category = categories.find((option) => option.category === broadcast.settingsCategory);

  const start = async () => {
    setIssues([]);
    try {
      // The server refuses a past time or one more than 30 days ahead, with its own message.
      const result = await startBroadcast({
        variables: { id: broadcast.id, expectedVersion: broadcast.revision, sendAt },
      });
      const started = result.data?.startPushBroadcast;
      if (started) {
        setShowConfirm(false);
        onStarted(started);
      }
    } catch (error) {
      setIssues(pushBroadcastErrorIssues(error));
    }
  };

  return (
    // The last part of the "Audience and send" section, right under the count it sends to: not a card of its own.
    <section>
      <h6 className="font-weight-bold mb-2">Send</h6>
      {blockers.length > 0 ? (
        <Alert variant="secondary" className="py-2">
          <div className="font-weight-bold">The send is locked:</div>
          <ul className="mb-0 pl-3">
            {blockers.map((blocker) => (
              <li key={blocker}>{blocker}</li>
            ))}
          </ul>
        </Alert>
      ) : null}
      {broadcast.resendWarning ? (
        <Alert variant="warning" className="py-2">
          {broadcast.resendWarning.message}
        </Alert>
      ) : null}
      <Form.Group className="mb-2">
        <Form.Check
          type="radio"
          id="push-send-now"
          name="push-send-when"
          label="Send now"
          checked={sendAt == null}
          onChange={() => setSendAt(null)}
        />
        <Form.Check
          type="radio"
          id="push-send-scheduled"
          name="push-send-when"
          label="Schedule (UTC)"
          checked={sendAt != null}
          onChange={() => setSendAt(defaultPushSendAt(Date.now()))}
        />
        {sendAt != null ? (
          <div className="mt-1 ml-4">
            <Form.Label htmlFor="push-send-at" className="small mb-1">
              Send at, <b>UTC</b> (up to 30 days ahead)
            </Form.Label>
            <PushUtcDateTime value={sendAt} onChange={setSendAt} inputId="push-send-at" />
            <small className="text-muted">
              {formatPushUtc(sendAt)}, {pushRelativeTime(sendAt, Date.now())}. Every check runs again when it fires;
              edits are blocked until then (take it back to draft to edit).
            </small>
          </div>
        ) : null}
      </Form.Group>
      <Button
        variant="danger"
        disabled={blockers.length > 0}
        onClick={() => {
          setIssues([]);
          setShowConfirm(true);
        }}
      >
        {sendAt == null ? 'Send…' : 'Schedule…'}
      </Button>

      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {sendAt == null ? 'Send' : 'Schedule'} &quot;{broadcast.name}&quot;?
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <dl className="row mb-0">
            <dt className="col-sm-4">Audience</dt>
            <dd className="col-sm-8">
              {audience ? (
                <>
                  ≈ {audience.count.toLocaleString()} players with a device, counted{' '}
                  {formatPushUtc(audience.computedAt)} by {pushPersonLabel(audience.computedBy, people)}.
                  <div className="small text-muted">
                    Approximate: the send recomputes it, and players who turned notifications off on their phone are
                    still in this number.
                  </div>
                </>
              ) : (
                'Not counted'
              )}
            </dd>

            <dt className="col-sm-4">Languages</dt>
            <dd className="col-sm-8">
              {translated.length} translated: {translated.map((code) => pushLanguageName(code, languages)).join(', ')}
              {overrides.length === 0 ? ' (English only)' : ''}.
              {audience ? (
                <div className="font-weight-bold">
                  {audience.receivingEnglishFallback.toLocaleString()} players will receive English.
                </div>
              ) : null}
            </dd>

            <dt className="col-sm-4">Opt-out category</dt>
            <dd className="col-sm-8">
              {category ? (
                <>
                  {category.label}
                  <div className="small text-muted">{category.description}</div>
                </>
              ) : (
                broadcast.settingsCategory
              )}
            </dd>

            <dt className="col-sm-4">Destination</dt>
            <dd className="col-sm-8">
              {broadcast.destination?.label ?? 'No deep link: a tap opens the game'}
              {broadcast.destination?.caveat ? (
                <div className="small text-warning">{broadcast.destination.caveat}</div>
              ) : null}
            </dd>

            <dt className="col-sm-4">When</dt>
            <dd className="col-sm-8">
              {sendAt == null ? (
                'Now'
              ) : (
                <>
                  <b>{formatPushUtc(sendAt)}</b> ({pushRelativeTime(sendAt, Date.now())})
                  <div className="small text-muted">
                    The audience is recomputed when it fires: the count above is from{' '}
                    {formatPushUtc(audience?.computedAt)}.
                  </div>
                </>
              )}
            </dd>
          </dl>

          {broadcast.resendWarning ? (
            <Alert variant="warning" className="mt-3 mb-0">
              <div className="font-weight-bold">These accounts were already sent a test</div>
              {broadcast.resendWarning.message}
              <div className="small">
                Test: &quot;{broadcast.resendWarning.sourceName}&quot; · {broadcast.resendWarning.userIds.join(', ')}
              </div>
            </Alert>
          ) : null}
          {broadcast.warnings.length > 0 ? (
            <PushIssueList
              issues={broadcast.warnings}
              title="Likely cut on a lock screen (not blocking)"
              variant="warning"
              className="mt-3 mb-0"
            />
          ) : null}
          <Alert variant="danger" className="mt-3 mb-0">
            A sent push cannot be recalled. Cancel stops the batches not sent yet, never the players already reached.
          </Alert>
          <PushIssueList issues={issues} title="The server refused the send" className="mt-3 mb-0" />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowConfirm(false)}>
            Back
          </Button>
          <Button variant="danger" disabled={starting} onClick={start}>
            {starting ? <Spinner animation="border" size="sm" className="mr-1" /> : null}
            {sendAt == null ? 'Send' : `Schedule for ${formatPushUtc(sendAt)}`} to ≈{' '}
            {(audience?.count ?? 0).toLocaleString()} players
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
